import { CircularProgress } from '@material-ui/core';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col } from 'react-bootstrap';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { AiOutlineSearch } from 'react-icons/ai';
import { toast } from 'react-toastify';
import {
  InputCidade,
  InputCpfCnpj,
  InputMaskCelular,
  InputMaskCep,
  InputMaskTelefone,
  InputSelect,
  InputText,
} from '~/components/NovosInputs';
import { SeparatorNovo } from '~/components/SeparatorNovo';
import { useFocusTabFinalizacao } from '~/pages/PdvOnline/hooks/useFocusTabFinalizacao';
import { usePdvOnline } from '~/pages/PdvOnline/hooks/usePdvOnline';
import { tabFinalizacaoServices } from '~/pages/PdvOnline/services/tab-finalizacao';
import {
  CadastroCliente,
  CidadeProps,
  SelectType,
} from '~/pages/PdvOnline/types';
import {
  pontuaCpfCnpj,
  validaCPF,
  validaEmail,
  validarCNPJ,
} from '~/utils/functions';
import { Modal } from '..';
import {
  BairroComplementoContainer,
  BairroComplementoContent,
  BasicContent,
  ButtonOk,
  ButtonOkContainer,
  ButtonSearchCep,
  ButtonSearchCepContainer,
  CepLogradouroContent,
  ClienteContainer,
  ContatoContent,
  EnderecoContainer,
  LogradouroNumeroContent,
  TelefoneCelularContent,
  TipoPessoaContent,
} from './styles';
import { resetCadastroCliente } from './utils/resetFields';
import { tipoPessoa } from './utils/tipoPessoa';

const autocompleteOffProps = { autoComplete: 'off' };

type RequiredAddressField =
  | 'num_cep'
  | 'des_logradouro'
  | 'num_endereco'
  | 'des_bairro'
  | 'des_complemento'
  | 'cidades';

const requiredAddressFields: RequiredAddressField[] = [
  'num_cep',
  'des_logradouro',
  'num_endereco',
  'des_bairro',
  'des_complemento',
  'cidades',
];

const hasCompleteCep = (value: unknown) =>
  typeof value === 'string' && value.replace(/\D/g, '').length === 8;

const hasSelectedCidade = (value: unknown) => {
  if (!value || typeof value !== 'object') return false;

  const cidade = value as Partial<CidadeProps> & {
    value?: number | string;
  };

  return (
    Boolean(cidade.label) ||
    cidade.cod_cidade !== undefined ||
    cidade.value !== undefined
  );
};

const isRequiredAddressFieldMissing = (
  field: RequiredAddressField,
  value: unknown,
) => {
  if (field === 'num_cep') return !hasCompleteCep(value);
  if (field === 'cidades') return !hasSelectedCidade(value);
  if (typeof value === 'string') return value.trim().length === 0;

  return value === undefined || value === null;
};

export const ModalCadastroCliente: React.FC = () => {
  const {
    isModalOpen,
    onCloseModal,
    isPdvOpen,
    handlePessoa,
    initialStatePdv,
  } = usePdvOnline();

  const { handleInputFocus } = useFocusTabFinalizacao();

  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cidadeSelected, setCidadeSelected] = useState<CidadeProps | undefined>(
    resetCadastroCliente.cidades,
  );
  const [cidade, setCidade] = useState<any>({
    cidade: '',
    uf: '',
  });
  const modalContentRef = useRef<HTMLFormElement | null>(null);
  const buttonOkRef = useRef<HTMLButtonElement | null>(null);
  const inputCidadeRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    control,
    setValue,
    watch,
    resetField,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm();

  const watchCep = watch('num_cep');

  const modalOpen =
    isPdvOpen && isModalOpen.isOpen && isModalOpen.modal === 'cadastroCliente';

  const { cadastrarCliente } = isModalOpen.data ?? {};

  useEffect(() => {
    if (!cadastrarCliente) return;

    setValue('num_cpf_cnpj', cadastrarCliente.num_cpf_cnpj);

    if (cadastrarCliente.num_cpf_cnpj?.length === 18) {
      setValue('tipo_pessoa', tipoPessoa[1]);
      return;
    }

    if (cadastrarCliente.num_cpf_cnpj?.length === 14) {
      setValue('tipo_pessoa', tipoPessoa[0]);
    }
  }, [cadastrarCliente, setValue]);

  useEffect(() => {
    if (inputCidadeRef.current) {
      const inputCidade: HTMLInputElement | null =
        inputCidadeRef.current.querySelector('[name="cidades"]');

      if (inputCidade && cidadeSelected) {
        const { label } = cidadeSelected;
        const { value } = inputCidade;

        if (value !== label) inputCidade.value = label;
      }
    }
  }, [cidadeSelected]);

  const handleCloseModal = useCallback(() => {
    onCloseModal();
    handleInputFocus('num_cpf_cnpj');
    setCidade(undefined);
    reset(resetCadastroCliente);
  }, [handleInputFocus, onCloseModal, reset]);

  useEffect(() => {
    if (!modalOpen) return;

    const inputName: HTMLInputElement | null = document.querySelector(
      '[name="nome_pessoa"]',
    );

    if (inputName) inputName.focus();
  }, [modalOpen]);

  useEffect(() => {
    const handleInputTabKey = (ev: KeyboardEvent) => {
      if (modalOpen && ev.key === 'Tab' && buttonOkRef.current) {
        if (document.activeElement === buttonOkRef.current) {
          if (modalContentRef.current) {
            const inputName: HTMLInputElement | null =
              modalContentRef.current.querySelector('[name="nome_pessoa"]');

            if (inputName) {
              setTimeout(() => {
                inputName.focus();
              }, 0);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleInputTabKey);
    return () => window.removeEventListener('keydown', handleInputTabKey);
  }, [modalOpen, buttonOkRef]);

  useEffect(() => {
    const handleCloseModalKey = (ev: KeyboardEvent) => {
      if (modalOpen && ev.key === 'Escape') {
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleCloseModalKey);
    return () => window.removeEventListener('keydown', handleCloseModalKey);
  }, [handleCloseModal, modalOpen]);

  const searchCep = useCallback(async () => {
    if (!watchCep || !modalOpen) return;

    const clearCep = watchCep.replace(/[^\d]/g, '');

    setBuscandoCep(true);

    try {
      // API VIACEP
      const data = await axios.get(
        `https://viacep.com.br/ws/${clearCep}/json/`,
      );
      if (!data) return;
      if (data.data && !data.data.erro) {
        setValue(
          'des_logradouro',
          data.data.logradouro && data.data.logradouro.toUpperCase(),
        );
        setValue(
          'des_bairro',
          data.data.bairro && data.data.bairro.toUpperCase(),
        );
        setCidade({
          cidade: data.data.localidade && data.data.localidade.toUpperCase(),
          uf: data.data.uf,
        });
      } else {
        setCidade({
          cidade: '',
          uf: '',
        });
        toast.warn('CEP não encontrado');
      }
    } catch (error) {
      resetField('des_logradouro');
      resetField('des_bairro');
      setCidade(undefined);
      toast.warning('CEP não encontrado');
    } finally {
      setBuscandoCep(false);
    }
  }, [modalOpen, resetField, setValue, watchCep]);

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    async (data) => {
      const values = data as CadastroCliente & { num_cpf_cnpj?: string };

      if (!values || !cadastrarCliente) return;

      const hasRequiredAddressError = requiredAddressFields.reduce(
        (hasError, field) => {
          const isFieldMissing = isRequiredAddressFieldMissing(
            field,
            values[field],
          );

          if (isFieldMissing) {
            setError(field, { type: 'required' });
          } else {
            clearErrors(field);
          }

          return hasError || isFieldMissing;
        },
        false,
      );

      if (values.num_cpf_cnpj) {
        const isCPF = values.num_cpf_cnpj.length === 14;
        const isCNPJ = values.num_cpf_cnpj.length === 18;
        const isInvalidType =
          typeof values.tipo_pessoa !== 'string' &&
          typeof values.tipo_pessoa !== 'number';

        const objTipoPessoa = (values.tipo_pessoa as SelectType) || {
          value: 0,
          label: 'Física',
        };

        if (isCPF && isInvalidType && Number(objTipoPessoa.value) !== 0) {
          handleInvalidType('CPF');
          return;
        }
        if (isCNPJ) {
          const isInvalid = handleInvalidTypeForCNPJ();
          if (isInvalid) return;
        }
      } else {
        clearErrors('tipo_pessoa');
      }

      function handleInvalidType(type: string) {
        toast.warning(`Tipo da Pessoa inválida para o ${type}`);
        setError('tipo_pessoa', { type: 'required' });
      }

      function handleInvalidTypeForCNPJ() {
        const isInvalid =
          typeof values.tipo_pessoa === 'string' ||
          typeof values.tipo_pessoa === 'undefined' ||
          (typeof values.tipo_pessoa !== 'string' &&
            typeof values.tipo_pessoa !== 'number' &&
            values.tipo_pessoa !== null &&
            values.tipo_pessoa !== undefined &&
            values.tipo_pessoa.value === 0);

        if (isInvalid) {
          handleInvalidType('CNPJ');
        }
        return isInvalid;
      }

      const numCpfCnpj =
        cadastrarCliente.num_cpf_cnpj === '' ||
        cadastrarCliente.num_cpf_cnpj === undefined
          ? values.num_cpf_cnpj
          : cadastrarCliente.num_cpf_cnpj;

      const isCnpj = (numCpfCnpj ?? '').replace(/[_\-./]/g, '').length > 11;

      const currentPayload: CadastroCliente = {
        ...values,
        num_cnpj: isCnpj ? numCpfCnpj : undefined,
        num_cpf: isCnpj ? undefined : numCpfCnpj,
      };

      if (modalContentRef.current) {
        const inputElement: HTMLInputElement | null =
          modalContentRef.current.querySelector(`[name="num_cpf_cnpj"]`);

        if (inputElement) {
          inputElement.focus();
          if (inputElement.value) inputElement.select();
        }

        if (
          (isCnpj && !currentPayload?.num_cnpj) ||
          (!isCnpj && !currentPayload?.num_cpf)
        ) {
          setError('num_cpf_cnpj', { type: 'required' });
          return;
        }
        clearErrors('num_cpf_cnpj');

        if (isCnpj && currentPayload.num_cnpj) {
          const valid = validarCNPJ(currentPayload.num_cnpj);
          if (!valid) {
            setError('num_cpf_cnpj', { type: 'required' });
            return;
          }
          clearErrors('num_cpf_cnpj');
        } else if (!isCnpj && currentPayload.num_cpf) {
          const valid = validaCPF(currentPayload.num_cpf);
          if (!valid) {
            setError('num_cpf_cnpj', { type: 'required' });
            return;
          }
          clearErrors('num_cpf_cnpj');
        }
      }

      if (currentPayload.email) {
        const valid = validaEmail(currentPayload.email);
        if (!valid) {
          setError('email', { type: 'value' });
          return;
        }
        clearErrors('email');
      }

      if (hasRequiredAddressError) return;

      const payload: CadastroCliente = {
        nome_pessoa: currentPayload.nome_pessoa || '',
        tipo_pessoa:
          typeof currentPayload.tipo_pessoa === 'string'
            ? tipoPessoa[0].value
            : currentPayload.tipo_pessoa,
        num_cpf: currentPayload.num_cpf,
        num_cnpj: currentPayload.num_cnpj,
        num_telefone: currentPayload.num_telefone,
        num_celular: currentPayload.num_celular,
        email: currentPayload.email,
        num_cep: currentPayload.num_cep,
        des_logradouro: currentPayload.des_logradouro,
        num_endereco: currentPayload.num_endereco,
        des_bairro: currentPayload.des_bairro,
        des_complemento: currentPayload.des_complemento,
        flg_endereco_principal: true,
        cod_loja: Number(initialStatePdv?.cod_loja),
        cidades: currentPayload.cidades,
      };

      const novoCliente = await tabFinalizacaoServices.insereCliente(payload);

      if (novoCliente.success) {
        let cpfCnpj: string;

        if (currentPayload.num_cpf) cpfCnpj = currentPayload.num_cpf;
        else if (currentPayload.num_cnpj) cpfCnpj = currentPayload.num_cnpj;
        else cpfCnpj = '';

        setCidade(undefined);
        reset(resetCadastroCliente);
        handleInputFocus('forma_finalizadora_0');
        handlePessoa({ num_cpf_cnpj: pontuaCpfCnpj(cpfCnpj) });

        onCloseModal();
        toast.success(novoCliente.message);
      } else {
        toast.warning(novoCliente.message);
      }
    },
    [
      cadastrarCliente,
      clearErrors,
      handleInputFocus,
      handlePessoa,
      onCloseModal,
      reset,
      setError,
      initialStatePdv?.cod_loja,
    ],
  );

  return (
    <Modal
      isOpen={modalOpen}
      title="Cadastrar Cliente"
      onClose={handleCloseModal}
      size="lg"
    >
      <ClienteContainer ref={modalContentRef} onSubmit={handleSubmit(onSubmit)}>
        <BasicContent>
          <InputText
            label="Nome"
            maxLength={100}
            placeholder="Informe o nome do cliente"
            name="nome_pessoa"
            toLowerCase={false}
            register={register}
            isError={!!errors.nome_pessoa}
            onInput={(ev: any) => {
              setValue('nome_pessoa', ev.target.value);
            }}
            id="nome_pessoa"
            isDisabled={isSubmitting}
          />
          <TipoPessoaContent>
            <InputSelect
              label="Tipo Pessoa"
              name="tipo_pessoa"
              placeholder={tipoPessoa[0].label}
              register={register}
              isError={!!errors.tipo_pessoa}
              control={control}
              options={tipoPessoa}
              changeSelected={(selected) => {
                setValue('tipo_pessoa', selected);
              }}
              id="tipo_pessoa"
              isDisabled={!!cadastrarCliente?.num_cpf_cnpj || isSubmitting}
            />
            <InputCpfCnpj
              label="CPF/CNPJ *"
              name="num_cpf_cnpj"
              id="num_cpf_cnpj"
              control={control}
              register={register}
              setValue={setValue}
              isError={!!errors.num_cpf_cnpj}
              isDisabled={!!cadastrarCliente?.num_cpf_cnpj || isSubmitting}
            />
          </TipoPessoaContent>
        </BasicContent>
        <SeparatorNovo
          leftLabel="Contato"
          rightLabel="Todos opcionais para cadastro"
          marginTop={20}
        />
        <ContatoContent>
          <TelefoneCelularContent>
            <InputMaskTelefone
              label="Telefone"
              name="num_telefone"
              isError={!!errors.num_telefone}
              control={control}
              {...autocompleteOffProps}
            />
            <InputMaskCelular
              label="Celular/WhatsApp"
              name="num_celular"
              isError={!!errors.num_celular}
              control={control}
              {...autocompleteOffProps}
            />
          </TelefoneCelularContent>
          <InputText
            label="Email"
            placeholder="Informe o E-mail"
            maxLength={100}
            name="email"
            toLowerCase={false}
            register={register}
            isError={!!errors.email}
            onInput={(ev: any) => {
              setValue('email', ev.target.value);
            }}
            id="email"
            isDisabled={isSubmitting}
            {...autocompleteOffProps}
          />
        </ContatoContent>
        <SeparatorNovo
          leftLabel="Endereço"
          rightLabel="Todos obrigatórios para cadastro"
          marginTop={20}
        />
        <EnderecoContainer>
          <CepLogradouroContent>
            <ButtonSearchCepContainer>
              <InputMaskCep
                label="CEP *"
                name="num_cep"
                isError={!!errors.num_cep}
                control={control}
                {...autocompleteOffProps}
              />
              <ButtonSearchCep
                aria-label="buscar cep"
                type="button"
                onClick={searchCep}
                disabled={buscandoCep || isSubmitting}
              >
                {!buscandoCep && <AiOutlineSearch size={20} color="#fff" />}
                {buscandoCep && (
                  <CircularProgress style={{ color: '#fff' }} size={15} />
                )}
              </ButtonSearchCep>
            </ButtonSearchCepContainer>
            <LogradouroNumeroContent>
              <InputText
                label="Logradouro *"
                placeholder="Informe o Logradouro"
                maxLength={100}
                name="des_logradouro"
                toLowerCase={false}
                register={register}
                isError={!!errors.des_logradouro}
                onInput={(ev: any) => {
                  setValue('des_logradouro', ev.target.value);
                }}
                disabled={buscandoCep || isSubmitting}
                {...autocompleteOffProps}
              />
              <InputText
                label="Número *"
                placeholder="Informe o Número"
                min={0}
                maxLength={15}
                name="num_endereco"
                toLowerCase={false}
                register={register}
                isError={!!errors.num_endereco}
                onInput={(ev: any) => {
                  setValue('num_endereco', ev.target.value);
                }}
                isDisabled={isSubmitting}
                {...autocompleteOffProps}
              />
            </LogradouroNumeroContent>
          </CepLogradouroContent>
          <BairroComplementoContainer>
            <BairroComplementoContent>
              <InputText
                label="Bairro *"
                placeholder="Informe o Bairro"
                maxLength={50}
                name="des_bairro"
                toLowerCase={false}
                register={register}
                isError={!!errors.des_bairro}
                onInput={(ev: any) => {
                  setValue('des_bairro', ev.target.value);
                }}
                disabled={buscandoCep || isSubmitting}
                {...autocompleteOffProps}
              />
              <InputText
                label="Complemento *"
                placeholder="Informe o Complemento"
                maxLength={50}
                name="des_complemento"
                toLowerCase={false}
                register={register}
                isError={!!errors.des_complemento}
                onInput={(ev: any) => {
                  setValue('des_complemento', ev.target.value);
                }}
                isDisabled={isSubmitting}
                {...autocompleteOffProps}
              />
            </BairroComplementoContent>
            <Col sm={12} md={4} ref={inputCidadeRef}>
              <InputCidade
                maxLength={29}
                setValue={setValue}
                label="Cidade *"
                placeholder="Selecione a Cidade"
                name="cidades"
                cidadeABuscar={cidade}
                register={register}
                clearCampo={cidade === undefined}
                isError={!!errors.cidades}
                control={control}
                disabled={buscandoCep || isSubmitting}
                {...autocompleteOffProps}
                changeSelected={(selected: any) => {
                  setValue('cidades', selected);
                  setCidade({
                    cidade: selected.des_cidade,
                    uf: selected.des_uf,
                  });
                  setCidadeSelected(selected);
                  clearErrors('cidades');
                }}
              />
            </Col>
          </BairroComplementoContainer>
        </EnderecoContainer>
        <ButtonOkContainer>
          <ButtonOk ref={buttonOkRef} type="submit" disabled={isSubmitting}>
            Ok
          </ButtonOk>
        </ButtonOkContainer>
      </ClienteContainer>
    </Modal>
  );
};
